import { useTranslation } from 'react-i18next';
import { ColumnDef } from '@tanstack/react-table';
import { useParamsSearch } from '@anytag/shared-hooks';
import { TableCell, TextCutter } from '@anytag/shared-atoms';
import { cn, formatIntNumber, formatPercent, getOffset } from '@anytag/shared-utils';
import { YoutubeAnalyticsTagRankingPayload, YoutubeAnalyticsTagRankingSortOrder } from '@anytag/graphql';

export const useInfluencerYoutubeTagsRankingTable = () => {
  const { t } = useTranslation();
  const { page, limit } = useParamsSearch();

  const firstIndex = page && limit ? getOffset(page, limit) + 1 : 1;
  const columns: ColumnDef<YoutubeAnalyticsTagRankingPayload>[] = [
    {
      accessorKey: 'tag',
      meta: { sticky: true },
      header: t<string>('HeaderColumn.Tag'),
      cell: ({ row }) => (
        <div className="flex w-55 items-center">
          <div
            className={cn(
              'mr-2 flex size-6 items-center justify-center text-lg font-semibold text-white',
              firstIndex + row.index <= 3 ? 'bg-red-70' : 'bg-black-40'
            )}
          >
            {firstIndex + row.index}
          </div>
          <TextCutter text={row.original.tag} />
        </div>
      )
    },
    {
      accessorKey: 'posts',
      header: t<string>('HeaderColumn.Post'),
      meta: {
        align: 'right',
        sortField: YoutubeAnalyticsTagRankingSortOrder.POST
      },
      cell: ({ row }) => <TableCell.Number value={formatIntNumber(row.original.posts)} />
    },
    {
      accessorKey: 'views',
      header: t<string>('HeaderColumn.View'),
      meta: {
        align: 'right',
        sortField: YoutubeAnalyticsTagRankingSortOrder.VIEWS
      },
      cell: ({ row }) => <TableCell.Number value={formatIntNumber(row.original.views)} />
    },
    {
      accessorKey: 'averageLike',
      header: t<string>('HeaderColumn.Ave like'),
      meta: {
        align: 'right',
        sortField: YoutubeAnalyticsTagRankingSortOrder.AVERAGE_LIKE
      },
      cell: ({ row }) => <TableCell.Number value={formatIntNumber(row.original.averageLike)} />
    },
    {
      accessorKey: 'averageDislike',
      header: t<string>('HeaderColumn.Ave dislike'),
      meta: {
        align: 'right',
        sortField: YoutubeAnalyticsTagRankingSortOrder.AVERAGE_DISLIKE
      },
      cell: ({ row }) => <TableCell.Number value={formatIntNumber(row.original.averageDislike)} />
    },
    {
      accessorKey: 'averageComment',
      header: t<string>('HeaderColumn.Ave Comment'),
      meta: {
        align: 'right',
        sortField: YoutubeAnalyticsTagRankingSortOrder.AVERAGE_COMMENT
      },
      cell: ({ row }) => <TableCell.Number value={formatIntNumber(row.original.averageComment)} />
    },
    {
      accessorKey: 'averageEngagement',
      header: t<string>('HeaderColumn.Ave Eng'),
      meta: {
        align: 'right',
        sortField: YoutubeAnalyticsTagRankingSortOrder.AVERAGE_ENGAGEMENT
      },
      cell: ({ row }) => <TableCell.Number value={formatIntNumber(row.original.averageEngagement)} />
    },
    {
      accessorKey: 'averageShare',
      header: t<string>('HeaderColumn.Ave share'),
      meta: {
        align: 'right',
        sortField: YoutubeAnalyticsTagRankingSortOrder.AVERAGE_SHARE
      },
      cell: ({ row }) => <TableCell.Number value={formatIntNumber(row.original.averageShare)} />
    },
    {
      accessorKey: 'engagementRate',
      header: t<string>('HeaderColumn.Eng rate'),
      meta: {
        align: 'right',
        sortField: YoutubeAnalyticsTagRankingSortOrder.ENGAGEMENT_RATE
      },
      cell: ({ row }) => <TableCell.Number value={formatPercent(row.original.engagementRate, false)} unit="%" />
    }
  ];

  return { columns };
};
